import { FC } from 'react'
import { Flex } from '@/components/atoms/Grid'
import styled from 'styled-components'
import { validFor } from '@/theme/validFor'

type CheckBoxProps = {
  size?: number
  sizeMobile?: number
  isChecked: boolean
  isValid?: boolean
  onCheckboxClick: () => void
} & Rest

enum ToggleAction {
  on = 'ON',
  off = 'OFF'
}

const ASPECT_RATIO = 37 / 16
const ASPECT_RATIO_MOBILE = 56 / 24
const defaultHeight = 16
const mobileHeight = 24

const ToggleCheckBoxRedesign: FC<CheckBoxProps> = ({
  size = defaultHeight,
  isChecked,
  onCheckboxClick,
  children,
  isValid = true,
  sizeMobile = mobileHeight,
  ...rest
}) => {
  const width = size * ASPECT_RATIO
  const mobileWidth = sizeMobile * ASPECT_RATIO_MOBILE

  return (
    <StyledFlex
      cursorOnHover={true}
      onClick={onCheckboxClick}
      {...rest}
      data-testid="checkBox"
    >
      {children}
      <ToggleWrapper
        isChecked={isChecked}
        isValid={isValid}
        height={size}
        width={width}
        mobileWidth={mobileWidth}
        mobileHeight={mobileHeight}
      >
        <ToggleBall
          isChecked={isChecked}
          width={width}
          height={size - 2}
          mobileHeight={mobileHeight - 2}
          mobileWidth={mobileWidth}
        />
        {isChecked ? (
          <ToggleText isChecked={isChecked}>{ToggleAction.on}</ToggleText>
        ) : (
          <ToggleText isChecked={isChecked}>{ToggleAction.off}</ToggleText>
        )}
      </ToggleWrapper>
    </StyledFlex>
  )
}

export default ToggleCheckBoxRedesign

const StyledFlex = styled(Flex)`
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  ${validFor.mobile`
    justify-content: space-between;
  `};
`

const ToggleWrapper = styled.div<{
  isChecked: boolean
  isValid: boolean
  width: number
  height: number
  mobileWidth: number
  mobileHeight: number
}>`
  background: ${(p) =>
    p.isChecked
      ? p.theme.colors.accentSecondaryNew
      : p.theme.colors.navMenuRed};
  border-radius: 4rem;
  height: ${(p) => p.height}px;
  width: ${(p) => p.width}px;
  position: relative;
  transition: cubic-bezier(0.56, 0, 0.38, 1) 0.3s;
  cursor: pointer;
  display: flex;
  align-items: center;
  box-shadow: 0 1px 3px 0 #00000033 inset;
  ${validFor.mobile`
     width: ${(p) => p.mobileWidth}px;
    height: ${(p) => p.mobileHeight}px;
  `};
`

const ToggleBall = styled.div<{
  height: number
  width: number
  isChecked: boolean
  mobileHeight: number
  mobileWidth: number
}>`
  background: #fff;
  border-radius: 50%;
  height: ${(p) => p.height}px;
  width: ${(p) => p.height}px;
  top: 50%;
  left: ${(p) =>
    p.isChecked ? `calc(${p.width}px - ${p.height}px - 1px)` : '1px'};
  position: absolute;
  transform: translateY(-50%);
  transition: cubic-bezier(0.56, 0, 0.38, 1) 0.3s;
  background: radial-gradient(
      97.22% 97.22% at 34.72% 0%,
      #eeeeee 0%,
      #e7e7e7 100%
    ),
    linear-gradient(0deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8));
  ${validFor.mobile`
    width: ${(p) => p.mobileHeight}px;
    height: ${(p) => p.mobileHeight}px;
    left: ${(p) =>
      p.isChecked
        ? `calc(${p.mobileWidth}px - ${p.mobileHeight}px - 1px)`
        : '1px'};
  `}
`

const ToggleText = styled.span`
  text-align: ${(p) => (p.isChecked ? 'left' : 'right')};
  padding-inline: 4px;
  width: 100%;
  font-size: 8px;
  font-weight: 700;
  color: white;
  pointer-events: none;
  ${validFor.mobile`
    font-size: 12px;
    padding-inline: 8px;
  `}
`
